import axios from "axios";

export interface IDeploymentLog {
  timestamp: string;
  message: string;
  type: 'info' | 'error' | 'success' | 'warning';
}

export interface IDeploymentStatus {
  isDeploying: boolean;
  deploymentStarted: string;
  logs: IDeploymentLog[];
}

export class ServerService {
  private static rootUrl = '/servers';

  static async getServers() {
    console.log("Hole Server-Daten...");
    try {
      const response = await axios.get(this.rootUrl);
      console.log("Server-Daten erfolgreich geladen:", response.data);
      return response.data;
    } catch (error) {
      console.error("Fehler beim Laden der Server-Daten:", error);
      throw error;
    }
  }

  static async createServer(serverData: { name: string; ip: string; user: string; password: string; description: string }) {
    console.log("Erstelle neuen Server:", serverData);
    try {
      const response = await axios.post(this.rootUrl, serverData);
      console.log("Server erfolgreich erstellt:", response.data);
      return response.data;
    } catch (error) {
      console.error("Fehler beim Erstellen des Servers:", error);
      throw error;
    }
  }

  static async updateServer(ref: string, updateData: Partial<{ name: string; ip: string; user: string; password: string; description: string }>) {
    console.log(`Aktualisiere Server ${ref}:`, updateData);
    try {
      const response = await axios.patch(`${this.rootUrl}/${ref}`, updateData);
      console.log(`Server ${ref} erfolgreich aktualisiert:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Servers ${ref}:`, error);
      throw error;
    }
  }

  static async deleteServer(ref: string) {
    console.log(`Lösche Server ${ref}...`);
    try {
      await axios.delete(`${this.rootUrl}/${ref}`);
      console.log(`Server ${ref} erfolgreich gelöscht`);
    } catch (error) {
      console.error(`Fehler beim Löschen des Servers ${ref}:`, error);
      throw error;
    }
  }

  static async assignWashpark(ref: string, accountId: string) {
    console.log(`Weise Server ${ref} den Waschpark mit AccountID ${accountId} zu...`);
    try {
      await axios.patch(`${this.rootUrl}/${ref}/washpark`, { accountId });
      console.log(`Waschpark erfolgreich zugewiesen für Server ${ref}`);
    } catch (error) {
      console.error(`Fehler beim Zuweisen des Waschparks für Server ${ref}:`, error);
      throw error;
    }
  }

  static async updateGitBranch(ref: string, gitBranch: string) {
    console.log(`Aktualisiere Git-Branch für Server ${ref} auf ${gitBranch}...`);
    try {
      await axios.patch(`${this.rootUrl}/${ref}/git-branch`, { gitBranch });
      console.log(`Git-Branch erfolgreich aktualisiert für Server ${ref}`);
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Git-Branches für Server ${ref}:`, error);
      throw error;
    }
  }

  static async deployServer(ref: string) {
    console.log(`Starte Deployment für Server ${ref}...`);
    try {
      // Längerer Timeout für Deployment-Startanfrage (auch wenn das Backend jetzt sofort antwortet)
      const response = await axios.post(`${this.rootUrl}/${ref}/deploy`, {}, {
        timeout: 30000, // 30 Sekunden Timeout für Deployment-Start
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log(`Deployment gestartet:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Deployment für Server ${ref}:`, error);
      throw error;
    }
  }

  static async getDeploymentLogs(ref: string, showFullHistory: boolean = false): Promise<IDeploymentStatus> {
    console.log(`Hole Deployment-Logs für Server ${ref}...${showFullHistory ? ' (vollständige Historie)' : ''}`);
    try {
      const response = await axios.get(`${this.rootUrl}/${ref}/deployment-logs${showFullHistory ? '?full=true' : ''}`);
      console.log(`Deployment-Logs erhalten:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Abrufen der Deployment-Logs für Server ${ref}:`, error);
      throw error;
    }
  }
  
  static async stopDeployment(ref: string) {
    console.log(`Stoppe Deployment für Server ${ref}...`);
    try {
      const response = await axios.post(`${this.rootUrl}/${ref}/stop-deployment`);
      console.log(`Deployment gestoppt:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Stoppen des Deployments für Server ${ref}:`, error);
      throw error;
    }
  }

  static async downloadDeploymentYaml(ref: string): Promise<void> {
    console.log(`Downloading deployment YAML for server ${ref}...`);
    try {
      const response = await axios.get(`${this.rootUrl}/${ref}/deployment-yaml`, {
        responseType: 'blob', // Important: we need to get the response as a blob
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/yaml' });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      a.download = `deployment-config-${ref}.yaml`; // Name of the downloaded file
      
      // Append the anchor to the body
      document.body.appendChild(a);
      
      // Click the anchor to trigger the download
      a.click();
      
      // Remove the anchor from the body
      document.body.removeChild(a);
      
      // Free up the blob URL
      window.URL.revokeObjectURL(url);
      
      console.log(`Deployment YAML successfully downloaded for server ${ref}`);
    } catch (error) {
      console.error(`Error downloading deployment YAML for server ${ref}:`, error);
      throw error;
    }
  }

  static async updateDeploymentVersions(ref: string, deploymentVersions: Record<string, string>) {
    console.log(`Aktualisiere deploymentVersions für Server ${ref}:`, deploymentVersions);
    try {
      const response = await axios.patch(`${this.rootUrl}/${ref}/deployment-versions`, deploymentVersions);
      console.log(`deploymentVersions für Server ${ref} erfolgreich aktualisiert:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren der deploymentVersions für Server ${ref}:`, error);
      throw error;
    }
  }

  static async deployServerV2(ref: string, deploymentVersions: Record<string, string>) {
    console.log(`Starte Deployment für Server ${ref}...`);
    try {
      const response = await axios.post(`${this.rootUrl}/${ref}/deployv2`, deploymentVersions);
      console.log(`Deployment gestartet:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Deployment für Server ${ref}:`, error);
      throw error;
    }
  }
}
